// Phase 5.4 · 간편 재고 뷰
// - 정상 재고 · 재고 부족 · 품절 · 발주 필요 요약
// - 재고 부족/발주 필요 상단 우선 표시
// - 각 상품 [발주하기] → 기존 PurchaseOrder 화면 이동
// - [상세 재고 관리] → 기존 InventorySection 렌더링
// - ?sku=... 자동 검색 지원 (기존 InventorySection 재사용을 위해 URL 유지)
// - BARCODE/WMS A4-1B · Add [입고 스캔] entry in the simple view header
//   (users spend most of their time here) + 4 summary cards deep-link into
//   the detail view with the matching filter pre-applied.
import React, { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, Package, XCircle, CheckCircle2, ShoppingCart, ChevronRight, PackagePlus, ScanLine } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import InboundScanModal from '@/components/seller/InboundScanModal';

const won = (n) => `${(Number(n) || 0).toLocaleString()}원`;

export default function SimpleInventory({ goTo, onOpenDetailedSection }) {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [poPending, setPoPending] = useState([]);
  const [loading, setLoading] = useState(true);
  // A4-1B · Inbound scan modal · reuses backend + engine already delivered
  // in A4-1.  NOT a new scanner — just an entry point on the simple view.
  const [inboundOpen, setInboundOpen] = useState(false);
  // A4-1B · re-fetch trigger so inbound-scan increments show in the counters
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [rInv, rOps] = await Promise.all([
          api.get('/seller/inventory', { params: { stock_status: 'low_stock', sort: 'recent' } }).catch(() => ({ data: { items: [] } })),
          api.get('/seller/dashboard/ops-summary').catch(() => ({ data: {} })),
        ]);
        if (!alive) return;
        // Try to also get all inventory summary
        try {
          const rAll = await api.get('/seller/inventory', { params: { sort: 'recent' } });
          setSummary(rAll.data?.summary || null);
        } catch { /* ignore */ }
        // Merge shortage list from OPS (prioritized) and low_stock list from inventory
        const opsShortage = Array.isArray(rOps.data?.stock_alerts) ? rOps.data.stock_alerts : [];
        const invRaw = rInv.data;
        const lowStock = Array.isArray(invRaw) ? invRaw
                        : Array.isArray(invRaw?.items) ? invRaw.items : [];
        // Deduplicate by product+variant
        const seen = new Set();
        const merged = [];
        for (const s of opsShortage) {
          const k = `${s.product_id}::${s.variant_key || 'default'}`;
          if (seen.has(k)) continue;
          seen.add(k);
          merged.push({
            product_id: s.product_id,
            variant_key: s.variant_key,
            product_name: s.product_name,
            available_stock: s.available_stock,
            unfulfilled_qty: s.unfulfilled_qty,
            recommended: s.recommended,
            status: 'shortage',
          });
        }
        for (const s of lowStock) {
          const k = `${s.product_id}::${s.variant_key || 'default'}`;
          if (seen.has(k)) continue;
          seen.add(k);
          merged.push({
            product_id: s.product_id,
            variant_key: s.variant_key,
            product_name: s.product_name,
            available_stock: s.available_stock,
            unfulfilled_qty: 0,
            recommended: 0,
            status: s.stock_status === 'sold_out' ? 'sold_out' : 'low_stock',
          });
        }
        setRows(merged);
        setPoPending(rOps.data?.po_pending_preview || []);
      } catch (e) { toast.error('재고 정보를 불러오지 못했습니다.'); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [reloadTick]);

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-gray-500 py-16 justify-center" data-testid="simple-inventory-loading"><Loader2 className="animate-spin" size={16} /> 불러오는 중…</div>;
  }

  const normalCnt = summary?.normal_count || 0;
  const lowCnt = summary?.low_stock_count || 0;
  const soldOutCnt = summary?.sold_out_count || 0;
  const reorderCnt = rows.length;

  // A4-1B · deep-link into detail view with the matching filter.  Reuses the
  // existing InventorySection · NO new page.  When goTo is provided by the
  // dashboard shell we prefer it (adds ?detail=1 + ?filter=...) so the shell
  // renders InventorySection with the URL synced; otherwise falls back to
  // onOpenDetailedSection.
  const openFiltered = (filter) => {
    if (typeof goTo === 'function') {
      goTo('inventory', { detail: 1, filter });
    } else if (typeof onOpenDetailedSection === 'function') {
      onOpenDetailedSection(filter);
    }
  };
  const openReorder = () => {
    if (typeof goTo === 'function') {
      goTo('inventory', { detail: 'purchase' });
    } else if (typeof onOpenDetailedSection === 'function') {
      onOpenDetailedSection();
    }
  };

  return (
    <div className="space-y-5" data-testid="simple-inventory-section">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-gray-400">재고</p>
          <h2 className="mt-1 text-2xl md:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            재고 관리
          </h2>
          <p className="mt-1 text-xs text-gray-500">부족한 상품과 발주가 필요한 상품을 우선 표시합니다.</p>
        </div>
        {/* A4-1B · Header actions · [입고 스캔] most visible (red) + detail link */}
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setInboundOpen(true)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#FF3B30] text-white text-xs font-black rounded-full hover:opacity-90"
                  data-testid="simple-inventory-inbound-scan">
            <ScanLine size={12} /> 입고 스캔
          </button>
          <button onClick={onOpenDetailedSection}
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-black/10 dark:border-white/10 text-xs font-bold rounded-full"
                  data-testid="simple-inventory-detail-btn">
            상세 재고 관리 →
          </button>
        </div>
      </div>

      {/* 4 카드 · A4-1B · 클릭 → 해당 필터로 상세 이동 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="simple-inventory-summary">
        <StatBox icon={CheckCircle2} color="emerald" label="정상 재고" value={normalCnt}
                 onClick={() => openFiltered('normal')} testId="inv-summary-normal" />
        <StatBox icon={AlertTriangle} color="amber" label="재고 부족" value={lowCnt}
                 onClick={() => openFiltered('low_stock')} testId="inv-summary-low" />
        <StatBox icon={XCircle} color="red" label="품절" value={soldOutCnt}
                 onClick={() => openFiltered('sold_out')} testId="inv-summary-soldout" />
        <StatBox icon={ShoppingCart} color="rose" label="발주 필요" value={reorderCnt}
                 onClick={openReorder} testId="inv-summary-reorder" />
      </div>

      {/* 재고 부족 / 발주 필요 상단 우선 표시 */}
      <div className="rounded-lg border-2 border-amber-400/50 bg-amber-50/60 dark:bg-amber-500/10 p-4" data-testid="simple-inventory-alerts">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={16} className="text-amber-600" />
          <p className="text-sm font-black tracking-widest uppercase text-amber-900 dark:text-amber-200">
            지금 처리할 상품 ({rows.length})
          </p>
        </div>
        {rows.length === 0 ? (
          <p className="text-xs text-amber-900/60 dark:text-amber-200/60 text-center py-2">재고가 부족한 상품이 없습니다. 좋아요!</p>
        ) : (
          <div className="space-y-1.5" data-testid="simple-inventory-shortage-list">
            {(Array.isArray(rows) ? rows : []).slice(0, 12).map((s) => (
              <div key={`${s.product_id}-${s.variant_key}`}
                className="flex flex-col md:flex-row md:items-center gap-2 p-2 rounded border border-amber-500/20 bg-white/70 dark:bg-black/20"
                data-testid={`simple-inventory-row-${s.product_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold truncate">{s.product_name || s.product_id}</p>
                  <p className="text-[11px] text-gray-500">
                    {s.variant_key && s.variant_key !== 'default' && <span>{s.variant_key} · </span>}
                    판매가능 <b className={`${s.available_stock <= 0 ? 'text-red-600' : 'text-amber-700'}`}>{s.available_stock}</b>
                    {s.unfulfilled_qty > 0 && <span> · 부족 <b className="text-red-600">{s.unfulfilled_qty}</b></span>}
                    {s.recommended > 0 && <span> · 권장 발주 <b className="text-[#FF3B30]">{s.recommended}</b></span>}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    s.status === 'sold_out'  ? 'bg-red-500/10 text-red-700' :
                    s.status === 'shortage' ? 'bg-amber-500/10 text-amber-700' :
                                             'bg-amber-500/10 text-amber-700'
                  }`}>
                    {s.status === 'sold_out' ? '품절' : s.status === 'shortage' ? '재고부족' : '부족'}
                  </span>
                  <button onClick={() => goTo && goTo('inventory', { detail: 'purchase' })}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#FF3B30] text-white text-[11px] font-black rounded-full"
                    data-testid={`simple-inventory-po-btn-${s.product_id}`}>
                    <PackagePlus size={11} /> 발주하기
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 승인 대기 발주 (있으면) */}
      {poPending.length > 0 && (
        <div className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-4" data-testid="simple-inventory-po-pending">
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-sm font-black tracking-widest uppercase">승인 대기 발주 ({poPending.length})</p>
            <button onClick={() => goTo && goTo('inventory', { detail: 'purchase' })} className="text-xs font-bold text-[#FF3B30] hover:underline" data-testid="simple-inventory-po-view-all">
              발주 관리 →
            </button>
          </div>
          <div className="space-y-1">
            {(Array.isArray(poPending) ? poPending : []).slice(0, 3).map((p) => (
              <div key={p.po_id} className="flex items-center justify-between text-xs p-2 rounded border border-black/5 dark:border-white/5">
                <span className="min-w-0 flex-1 truncate">
                  <b>{p.po_number}</b>
                  <span className="text-gray-500"> · {(p.supplier_snapshot || {}).name || '-'} · {p.item_count}건</span>
                </span>
                <b>{won(p.total_amount || 0)}</b>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 하단 상세 관리 링크 */}
      <div className="text-center pt-2">
        <button onClick={onOpenDetailedSection} className="text-xs font-bold text-gray-500 hover:text-[#FF3B30] hover:underline" data-testid="simple-inventory-detail-bottom">
          전체 재고 · SKU · 옵션재고 · 재고조정 <b>상세 관리 →</b>
        </button>
      </div>

      {inboundOpen && (
        <InboundScanModal
          onClose={() => setInboundOpen(false)}
          onSaved={() => setReloadTick((t) => t + 1)}
        />
      )}
    </div>
  );
}

const StatBox = ({ icon: Icon, color, label, value, testId, onClick }) => {
  const clsMap = {
    emerald: 'text-emerald-700 border-emerald-200 bg-emerald-50/70 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-300',
    amber:   'text-amber-700 border-amber-200 bg-amber-50/70 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300',
    red:     'text-red-700 border-red-200 bg-red-50/70 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-300',
    rose:    'text-rose-700 border-rose-200 bg-rose-50/70 dark:bg-rose-500/10 dark:border-rose-500/30 dark:text-rose-300',
  };
  const clickable = typeof onClick === 'function';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`rounded-lg border-2 p-3 text-left w-full transition ${clsMap[color]} ${clickable ? 'hover:brightness-95 hover:shadow-md active:scale-[0.98] cursor-pointer' : 'cursor-default'}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5">
        <Icon size={13} />
        <p className="text-[11px] font-bold uppercase tracking-widest">{label}</p>
        {clickable && <ChevronRight size={11} className="ml-auto opacity-40" />}
      </div>
      <p className="mt-1 text-2xl font-black tabular-nums">
        {(value || 0).toLocaleString()}<span className="text-xs font-bold text-gray-400 ml-0.5">건</span>
      </p>
    </button>
  );
};
